import { Injectable } from '@angular/core'
import { HttpClient, HttpParams } from '@angular/common/http'
import { catchError, map, Observable, of, shareReplay, switchMap } from 'rxjs'
import { Contract } from '../store/interfaces/contract'
import { environment } from '../../environments/environment'
import { ContractOperatorService } from './contract-operator.service'
import { unwrapCrudList, type CrudListResponse } from '../common/utils/crud-response.util'
import { extractContractIdsFromUser } from '../permissions/data-scope.util'
import { Doctor } from '../store/interfaces/doctor'
import { Operator } from '../store/interfaces/operator'
import { DataScope } from '../permissions/permission.types'
@Injectable({ providedIn: 'root' })
export class ContractsService {
  private apiUrl = `contracts`
  private readonly base = `${environment.apiUrl}/`
  private readonly contractsByIdsCache = new Map<string, { cachedAt: number; value$: Observable<Contract[] | null> }>()
  private cachedContracts$: Observable<Contract[]> | null = null
  private cachedContractsAt = 0
  private readonly cacheTtlMs = 5 * 60 * 1000

  constructor(
    private contractOperatorService: ContractOperatorService,
    private http: HttpClient,
  ) {}

  getContracts(options: { propagateErrors?: boolean } = {}): Observable<Contract[]> {
    const now = Date.now()
    if (!this.cachedContracts$ || now - this.cachedContractsAt >= this.cacheTtlMs) {
      this.cachedContractsAt = now
      this.cachedContracts$ = this.requestContracts().pipe(
        shareReplay({ bufferSize: 1, refCount: false }),
        catchError((err) => {
          this.cachedContracts$ = null
          this.cachedContractsAt = 0
          throw err
        }),
      )
    }
    const contracts$ = this.cachedContracts$
    if (options.propagateErrors) return contracts$

    return contracts$.pipe(
      catchError((err) => {
        console.error('Error cargando contratos', err)
        return of([])
      }),
    )
  }

  private requestContracts(): Observable<Contract[]> {
    let params = new HttpParams()
    params = params.append('join', 'channels')
    params = params.append('join', 'channels.channel')
    params = params.append('join', 'specialties')
    params = params.append('join', 'specialties.specialty')
    params = params.set('limit', '1000')

    return this.http.get<Contract[]>(`${this.base}${this.apiUrl}`, { params })
  }

  getContractsByIds(ids: string[]): Observable<Contract[] | null> {
    const normalizedIds = Array.from(new Set(ids.map((id) => String(id).trim()).filter(Boolean))).sort()
    const cacheKey = normalizedIds.join(',')
    const cached = this.contractsByIdsCache.get(cacheKey)
    if (cached && Date.now() - cached.cachedAt < this.cacheTtlMs) {
      return cached.value$
    }

    const params = new HttpParams()
      .append('join', 'channels')
      .append('join', 'channels.channel')
      .append('join', 'specialties')
      .append('join', 'specialties.specialty')
      .append('filter', `id||$in||${normalizedIds.join(',')}`)
      .set('limit', '1000')

    const value$ = this.http.get<CrudListResponse<Contract>>(`${this.base}${this.apiUrl}`, { params }).pipe(
      map((res) => {
        const contracts = unwrapCrudList(res)
        return contracts.length > 0 ? contracts : null
      }),
      shareReplay({ bufferSize: 1, refCount: false }),
      catchError((err) => {
        this.contractsByIdsCache.delete(cacheKey)
        throw err
      }),
    )
    this.contractsByIdsCache.set(cacheKey, { cachedAt: Date.now(), value$ })
    return value$
  }

  getContractsForOperator(operatorId: number | string): Observable<Contract[]> {
    return this.contractOperatorService.getByOperatorId(operatorId).pipe(
      // Nos quedamos con los IDs de contrato únicos
      map((links) => Array.from(new Set(links.map((l) => l.contractId))).filter(Boolean)),
      switchMap((ids) => {
        if (!ids.length) return of<Contract[]>([])
        return this.getContractsByIds(ids).pipe(map((contracts) => contracts ?? []))
      }),
      catchError((err) => {
        console.error('Error cargando contratos del operador', err)
        return of([])
      }),
    )
  }

  filterActiveContracts(contracts: Contract[]): Contract[] {
    return contracts.filter((contract) => contract.isActive)
  }

  filterContractsByScope(
    contracts: Contract[],
    isGlobalScope: boolean,
    user: Doctor | Operator | null | undefined,
    scope?: DataScope,
  ): Contract[] {
    const active = this.filterActiveContracts(contracts)
    if (isGlobalScope) return active
    const allowedContractIds = extractContractIdsFromUser(user, scope)
    if (!allowedContractIds.length) return []
    return active.filter((contract) => allowedContractIds.includes(String(contract.id)))
  }

  filterHeadContractOptions(
    contracts: Contract[],
    isGlobalScope: boolean,
    user: Doctor | Operator | null | undefined,
  ): Contract[] {
    return this.filterContractsByScope(contracts, isGlobalScope, user)
  }

  mergeSelectedContracts(options: Contract[], selectedIds: string[], allContracts: Contract[]): Contract[] {
    const byId = new Map(options.map((contract) => [String(contract.id), contract]))
    for (const id of selectedIds) {
      if (!byId.has(id)) {
        const found = allContracts.find((contract) => String(contract.id) === id)
        if (found) byId.set(id, found)
      }
    }
    return Array.from(byId.values())
  }

  getAssignableContracts(
    isGlobalScope: boolean,
    user: Doctor | Operator | null | undefined,
    scope?: DataScope,
    options: { propagateErrors?: boolean } = {},
  ): Observable<Contract[]> {
    const allowedContractIds = extractContractIdsFromUser(user, scope)
    const request$ = isGlobalScope
      ? options.propagateErrors
        ? this.requestContracts()
        : this.getContracts()
      : allowedContractIds.length
        ? options.propagateErrors
          ? this.requestContracts()
          : this.getContracts()
        : of([] as Contract[])
    const source$ =
      options.propagateErrors || isGlobalScope
        ? request$
        : request$.pipe(
            catchError((err) => {
              console.error('Error cargando contratos asignables', err)
              return of([] as Contract[])
            }),
          )

    return source$.pipe(map((contracts) => this.filterContractsByScope(contracts, isGlobalScope, user, scope)))
  }

  getHeadContractOptions(
    isGlobalScope: boolean,
    user: Doctor | Operator | null | undefined,
    preserveIds: string[] = [],
  ): Observable<Contract[]> {
    const allowedContractIds = extractContractIdsFromUser(user)
    const source$ = isGlobalScope
      ? this.getContracts()
      : allowedContractIds.length
        ? this.getContracts()
        : of([] as Contract[])

    return source$.pipe(
      map((contracts) => {
        const filtered = this.filterHeadContractOptions(contracts, isGlobalScope, user)
        return preserveIds.length ? this.mergeSelectedContracts(filtered, preserveIds, contracts) : filtered
      }),
    )
  }
}
